import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get, isNil } from 'lodash';
import cn from 'classnames';
import Datetime from 'react-datetime';
import moment from 'moment';

import ApplicationId from 'components/ApplicationId';
import Transition from 'components/Transition';
import Modal from 'components/Modal';
import Button from 'components/Button';
import Header from 'components/Header';
import Progress from 'components/Progress';
import FormRow from 'components/Form/FormRow';
import FormColumn from 'components/Form/FormColumn';
import FormGroup from 'components/Form/FormGroup';
import ContentWrapper from 'components/ContentWrapper';
import Validator from 'components/Validator';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import CardNumberInput from '@lendingusa/react-common-card-number-input';
import imgError from 'assets/images/error.svg';
import editIcon from 'assets/images/edit.png';
import cancelEditIcon from 'assets/images/cancel-edit.png';
import NoAchIcon from 'assets/icons/debit_card.svg';
import XIcon from 'assets/icons/x.svg';
import VerifiedIcon from 'assets/images/correct.png';
import WarningIcon from 'assets/images/warning.png';

import {
  accountMask,
  routingNumberMask,
  currencyMask,
  numberUnmask,
} from 'utils/masks';

import { formatCurrency } from 'utils/formatCurrency';
import { getOrConvertToPst } from 'utils/timezone';
import { newNextAction, validateBankRoutingNumberAction } from '../../../actions/navigation';

import schema from './debitCardSchema';
import './style.scss';

const INITIAL_VALIDATION_STATE = {
  loading: false,
  verified: false,
  checkedFirstTime: false,
  data: null,
};

const DebitCardElection = ({
  validator: { values, errors, onChangeHandler, setValues, validate, setErrors, deleteErrors },
  checkinData,
  navigate,
  dispatch,
  params,
  searchParams,
  location,
}) => {
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(0);
  const [firstPaymentDate, setFirstPaymentDate] = useState(getOrConvertToPst().add(37, 'd'));
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [isDatePickVisible, setIsDatePickVisible] = useState(false);
  const [isDisclosureVisible, setIsDisclosureVisible] = useState(false);
  const [isGiactPopupVisible, setIsGiactPopupVisible] = useState(false);
  const [giactPopupAttrs, setGiactPopupAttrs] = useState(null);
  const [giactMessage, setGiactMessage] = useState(null);
  const [isNoAutopayPopupVisible, setIsNoAutopayPopupVisible] = useState(false);
  const [routingValidateStatus, setRoutingValidateStatus] = useState(INITIAL_VALIDATION_STATE);
  const key = searchParams.get('key');
  const merchantName = get(checkinData, 'data.data.merchantName', get(checkinData, 'state.data.merchantName', ''));
  const isAutoPayOffer = get(checkinData, 'data.data.isAutoPayOffer', get(checkinData, 'state.data.isAutoPayOffer', ''));
  const zipTerm = get(checkinData, 'data.data.zipTerm', get(checkinData, 'state.data.zipTerm'));
  const originationPercentage = get(checkinData, 'data.data.originationPercentage') || get(checkinData, 'data.data.originationPercentage');
  const workflowType = get(params, 'workflowType') || 'text2apply';

  const paymentData = useRef({
    isBankAccountExisting: false,
    isDebitCardExisting: false,
  });

  const noMatchingOffer = !isAutoPayOffer && isNil(zipTerm);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const sourceData = get(checkinData, ['data', 'data']) || get(checkinData, ['state', 'data']);

    if (sourceData === undefined) {
      navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}`);
    } else {
      const dataObj = get(checkinData, 'data.data') || get(checkinData, 'state.data');
      const accounts = get(dataObj, 'bankAccounts', undefined);
      const primaryAccountIndex = (accounts || []).findIndex((acc) => acc.isPrimary);
      const payFromAccount = accounts ? accounts[primaryAccountIndex === -1 ? 0 : primaryAccountIndex] : get(dataObj, 'bankAccount', {});
      const debitCard = get(dataObj, 'debitCard');
      const error = get(dataObj, 'errors.0') || get(checkinData, 'failure.errors.0', '');

      if (error) {
        setHasError(true);
        setErrorMessage(get(error, 'message'));
      }

      let initialBankData = {
        accounts,
        payFromAccount: JSON.stringify(payFromAccount),
        firstPaymentDate: getOrConvertToPst().add(37, 'd'),
        consentDate: getOrConvertToPst().format('MM/DD/YYYY'),
        isEditingDebitCard: false,
        isEditingBankInfo: false,
        debitCard: {
          accountHolderName: `${get(dataObj, 'applicant.firstName', '') || ''} ${get(dataObj, 'applicant.lastName', '') || ''}`,
        },
      };

      if (payFromAccount) {
        initialBankData = {
          ...initialBankData,
          accountId: get(payFromAccount, 'accountId'),
          autoBankAccount: {
            accountNumber: get(payFromAccount, 'accountNumber', '') || '',
            routingNumber: get(payFromAccount, 'routingNumber', '') || '',
            accountType: get(payFromAccount, 'accountType', 'Checking') || 'Checking',
            accountHolderName: get(payFromAccount, 'accountHolderName', '') || '',
            bankName: get(payFromAccount, 'bankName', '') || '',
          },
          isBankAccountExisting: true,
        };
        paymentData.current.isBankAccountExisting = true;
        setRoutingValidateStatus({
          ...routingValidateStatus,
          verified: true,
          checkedFirstTime: true,
        });
      } else {
        initialBankData.isBankAccountExisting = false;
        paymentData.current.isBankAccountExisting = false;
      }

      if (debitCard) {
        const expYear = get(debitCard, 'expirationDate.year', 0);
        const expMonth = get(debitCard, 'expirationDate.month', 0);

        initialBankData = {
          ...initialBankData,
          debitCard: {
            ...initialBankData.debitCard,
            cardNumber: get(debitCard, 'cardNumberLast4', '') || '',
            expirationDate: moment(`${expMonth}/${expYear}`, 'MM/YYYY').format('MM/YY'),
          },
          isDebitCardExisting: true,
        };
        paymentData.current.isDebitCardExisting = true;
      } else {
        initialBankData.isDebitCardExisting = false;
        paymentData.current.isDebitCardExisting = false;
      }

      if (get(dataObj, 'isAutoPay')) {
        initialBankData.isSelfSubmitted = get(dataObj, 'isDebitCard') ? 1 : 2;
      } else {
        initialBankData.isSelfSubmitted = isAutoPayOffer ? 1 : 2;
      }

      setValues(initialBankData);
    }
  }, []);

  const handleSubmitForm = (flag = false, e) => {
    e.preventDefault();

    if (key) {
      if (schema && validate(schema).isValid) {
        setIsProgressVisible(true);

        const datePattern = 'MM/DD/YYYY';

        let payloadData = {
          isAutoPay: values.isSelfSubmitted === 1 || values.isSelfSubmitted === 2,
          firstPaymentDate: moment(values.firstPaymentDate).format(datePattern),
          iframe: 3,
        };

        if (values.isSelfSubmitted === 1) {
          payloadData = {
            ...payloadData,
            isDebitCard: true,
          };
        } else if (values.isSelfSubmitted === 2) {
          payloadData = {
            ...payloadData,
            isDebitCard: false,
          };
        } else if (values.isSelfSubmitted === 3) {
          payloadData = {
            ...payloadData,
            isDebitCard: false,
          };
        }

        if (flag) {
          payloadData = {
            ...payloadData,
            isDebitCard: false,
            isAutoPay: false,
          };
        }

        if (!paymentData.current.isDebitCardExisting || values.isEditingDebitCard) {
          const dateSplit = get(values, 'debitCard.expirationDate').split('/');
          const normalizedDate = `${dateSplit[0]}/20${dateSplit[1]}`;
          const expirationDateMoment = moment(normalizedDate, 'MM/YYYY');

          payloadData.DebitCard = {
            AccountHolderName: get(values, 'debitCard.accountHolderName', ''),
            CardNumber: numberUnmask(get(values, 'debitCard.cardNumber', '')),
            Expiration: {
              Month: expirationDateMoment.month() + 1,
              Year: expirationDateMoment.year(),
            },
          };
        }

        if (values.isSelfSubmitted === 2 && !flag) {
          if (!paymentData.current.isBankAccountExisting || values.isEditingBankInfo) {
            payloadData.AutoBankAccount = {
              AccountNumber: get(values, 'autoBankAccount.accountNumber', ''),
              RoutingNumber: get(values, 'autoBankAccount.routingNumber', ''),
              AccountType: get(values, 'autoBankAccount.accountType', ''),
              AccountHolderName: get(values, 'autoBankAccount.accountHolderName', ''),
              BankName: get(values, 'autoBankAccount.bankName', ''),
            };
          } else {
            payloadData.accountId = get(values, 'accountId');
          }
        }

        if (values.avsFailed) {
          payloadData.DebitCard = {
            ...(payloadData.DebitCard || {}),
            owner: {
              ...values.owner,
            },
          };
        }

        searchParams.get('isEmail') && Object.assign(payloadData, { isEmail: true });
        setHasError(false);
        setErrorMessage();
        setValues({ ...values });

        dispatch(newNextAction({
          key,
          workflowType,
          data: payloadData,
          onUploadProgress: (progressEvent) => {
            setUploadingPercentage(
              Math.floor((progressEvent.loaded * 100) / progressEvent.total)
            );
          },
          success: (res) => {
            const routeUrl = get(res, 'data.url');
            navigate(routeUrl);
          },
          fail: (err) => {
            setIsProgressVisible(false);

            if (get(err, 'response.data.failure.errors.0.subject') === 'bankAccountVerificationFailed') {
              setIsGiactPopupVisible(true);
              setGiactPopupAttrs(get(err, 'response.data.failure.errors.0.attributes'));
              setGiactMessage(get(err, 'response.data.failure.errors.0.message'));
            } else {
              setHasError(true);

              if (get(err, 'response.data.failure.errors.0')) {
                setErrorMessage(get(err, 'response.data.failure.errors.0.message'));

                if (get(err, 'response.data.failure.errors.0.subject') === 'avsFailed') {
                  setValues({ ...values, avsFailed: true });
                }
              } else {
                setErrorMessage('An unexpected error happened while saving the payment information. Please try again later.');
              }
            }
          },
        }));
      } else {
        validate(schema);
      }
    }
  };

  const validateRoutingNo = (routingNumber) => {
    const newValue = routingNumber.replace(/[^\d]/g, '');
    if (newValue.length === 9) {
      return true;
    }

    if (routingValidateStatus.checkedFirstTime) {
      setRoutingValidateStatus({ ...routingValidateStatus, verified: false });
    }
    return false;
  };

  const validateRoutingNumber = (routingNo) => {
    let routingNumberUnMask;
    if (routingNo && routingNo.length === 9) {
      routingNumberUnMask = routingNo.replace(/[^\d]/g, '');
    }
    if (!routingNumberUnMask || routingNumberUnMask.length !== 9) return;
    setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, loading: true, data: null });
    if (routingNo) {
      dispatch(validateBankRoutingNumberAction({
        routingNumber: routingNo,
        workflowType,
        success: (res) => {
          const validateRespose = res && res.length > 0 ? res[0] : null;
          const validateStatus = validateRespose && validateRespose.status;
          const validateData = validateRespose && validateRespose.data;

          if (validateStatus && validateStatus === 'success') {
            setValues({
              ...values,
              autoBankAccount: {
                ...values.autoBankAccount,
                bankName: validateData.name,
                routingNumber: routingNo,
              },
              isValidRoutingNumber: true,
            });
            deleteErrors(['autoBankAccount.routingNumber']);
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: true, loading: false, data: validateData });
          }
          if (validateStatus && validateStatus === 'fail') {
            setErrors({ ...errors, 'autoBankAccount.routingNumber': validateData.message || 'An error occurred' });
            setValues({
              ...values,
              autoBankAccount: {
                ...values.autoBankAccount,
                bankName: '',
                routingNumber: routingNo,
              },
              isValidRoutingNumber: false,
            });
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
          }
        },
        fail: () => {
          setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
        },
      }));
    }
  };

  const prefillBankAccount = (accountIndex) => {
    const accounts = get(values, 'accounts', []);
    const bankData = accounts.find((acc) => acc.accountId === accountIndex);

    if (bankData) {
      setValues({
        ...values,
        accountId: get(bankData, 'accountId'),
        payFromAccount: JSON.stringify(bankData),
        autoBankAccount: {
          accountHolderName: get(bankData, 'accountHolderName') || '',
          bankName: get(bankData, 'bankName') || '',
          routingNumber: get(bankData, 'routingNumber') || '',
          accountNumber: get(bankData, 'accountNumber') || '',
          accountType: get(bankData, 'accountType') || '',
        },
      });
    }
  };

  const handleInputChange = useCallback((event) => {
    switch (event.target.name) {
      case 'accountId':
        prefillBankAccount(event.target.value);
        break;
      case 'autoBankAccount.routingNumber':
        if (!routingValidateStatus.loading) {
          onChangeHandler(event.target.name, event.target.value);
          if (validateRoutingNo(event.target.value) && event.target.value !== values.routingNumber) {
            validateRoutingNumber(event.target.value);
          }
        }
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  }, [onChangeHandler]);

  const toggleGiactPopup = useCallback((e) => {
    e.preventDefault();

    setIsGiactPopupVisible(false);
  }, []);

  const handleConfirmDate = () => {
    setIsDatePickVisible(false);
    onChangeHandler('firstPaymentDate', firstPaymentDate);
  };

  const handleDateChange = (date) => {
    setFirstPaymentDate(date);
  };

  const toggleDebitCardEdit = () => {
    const dataObj = get(checkinData, 'data.data', {}) || get(checkinData, 'state.data');
    const debitCardData = get(dataObj, 'debitCard', null);

    if (!values.isEditingDebitCard) {
      setValues({
        ...values,
        debitCard: {
          ...values.debitCard,
          cardNumber: '',
          expirationDate: '',
        },
        isEditingDebitCard: !values.isEditingDebitCard,
      });
    } else {
      const expYear = get(debitCardData, 'expirationDate.year', 0);
      const expMonth = get(debitCardData, 'expirationDate.month', 0);

      setValues({
        ...values,
        debitCard: {
          ...values.debitCard,
          cardNumber: get(debitCardData, 'cardNumberLast4', ''),
          expirationDate: moment(`${expMonth}/${expYear}`, 'MM/YYYY').format('MM/YY'),
        },
        isEditingDebitCard: !values.isEditingDebitCard,
      });
    }
  };

  const toggleBankInfoEdit = () => {
    const payFromAccount = values.payFromAccount ? JSON.parse(values.payFromAccount) : null;

    if (!values.isEditingBankInfo) {
      setValues({
        ...values,
        autoBankAccount: {
          accountNumber: '',
          routingNumber: '',
          accountType: 'Checking',
          accountHolderName: '',
          bankName: '',
        },
        isEditingBankInfo: !values.isEditingBankInfo,
      });
    } else {
      setValues({
        ...values,
        autoBankAccount: {
          accountNumber: get(payFromAccount, 'accountNumber', ''),
          routingNumber: get(payFromAccount, 'routingNumber', ''),
          accountType: get(payFromAccount, 'accountType', 'Checking'),
          accountHolderName: get(payFromAccount, 'accountHolderName', ''),
          bankName: get(payFromAccount, 'bankName', ''),
        },
        isEditingBankInfo: !values.isEditingBankInfo,
      });
      deleteErrors(['autoBankAccount.routingNumber', 'autoBankAccount.accountHolderName', 'autoBankAccount.accountNumber']);
    }
  };

  const handleCheckboxChange = (name, value) => {
    onChangeHandler(name, value);

    if (name === 'isSelfSubmitted' && value === 2 && !values.autoBankAccount) {
      onChangeHandler('autoBankAccount.accountType', 'Checking');
      setValues({
        ...values,
        isSelfSubmitted: value,
        autoBankAccount: {
          ...(values.autoBankAccount || {}),
        },
      });
    } else if (name === 'isSelfSubmitted' && value === 3) {
      setIsNoAutopayPopupVisible(true);
      onChangeHandler(name, value);
    } else {
      onChangeHandler(name, value);
    }
  };

  const getCardSubtitle = () => {
    let subtitle;
    if (zipTerm >= 1 && originationPercentage === 0) {
      subtitle = `0% APR if paid off within ${zipTerm} months*`;
    } else if (zipTerm >= 1 && originationPercentage > 0) {
      subtitle = `NO INTEREST if paid off within ${zipTerm} months*`;
    }
    return subtitle;
  };

  const renderMethodOption = (optionIndex) => (
    <div className={cn('method-option-item text-center', values.isSelfSubmitted === optionIndex && 'selected')} onClick={handleCheckboxChange.bind(null, 'isSelfSubmitted', optionIndex)}>
      {optionIndex === 2 ? (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M3 8v12h18V8M3 8l9-4 9 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M2 10h20" stroke="currentColor" strokeWidth="2" />
        </svg>
      )}
      <h4>{optionIndex === 2 ? 'Bank Account' : 'Debit Card'}</h4>
      <p>{getCardSubtitle()}</p>
    </div>
  );

  const handleFocus = () => {
    setIsDatePickVisible(true);
  };

  const handleCloseModal = () => {
    setIsDatePickVisible(false);
    setFirstPaymentDate(values.firstPaymentDate);
  };

  const handleClosePopup = () => {
    setIsDisclosureVisible(false);
  };

  const togglePopup = (e) => {
    e.preventDefault();
    setIsDisclosureVisible(!isDisclosureVisible);
  };

  const toggleNoAutopayPopup = (e) => {
    e.preventDefault();
    setIsNoAutopayPopupVisible(!isNoAutopayPopupVisible);
  };

  const handleUseDebitCard = (e) => {
    e.preventDefault();
    onChangeHandler('isSelfSubmitted', 1);
    setIsNoAutopayPopupVisible(false);
  };

  const downPayment = get(checkinData, 'data.data.downPaymentRequired', 0);
  const { isDebitCardExisting, isBankAccountExisting } = paymentData.current;

  const [cardError, setCardError] = useState('');

  const handleCardChange = (digits) => {
    onChangeHandler('debitCard.cardNumber', digits);
  };

  const handleCardComplete = (digits, isValid) => {
    if (!isValid) {
      setErrors({ ...errors, 'debitCard.cardNumber': 'Invalid card number' });
    } else {
      deleteErrors(['debitCard.cardNumber']);
    }
  };

  const handleCardBlur = (digits, isValid) => {
    if (digits && !isValid) {
      setErrors({ ...errors, 'debitCard.cardNumber': 'Invalid card number' });
    }
  };

  const handleCardBrandChange = (brand, isAllowed) => {
    if (brand && !isAllowed) {
      setCardError('Currently we accept only Discover, Visa or Mastercard');
    } else {
      setCardError('');
    }
  };

  const handleCardCancel = () => {
    toggleDebitCardEdit();
  };

  const handleCardRevert = () => {
    toggleDebitCardEdit();
  };

  return (
    <section className="debit-card-election dponly-payment-page autopay-election-page">
      <Transition className="transition-item wrapper v-center">
        <Header />
        <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10, padding: '0 7px' }} />
        <hr className="separator pt-20" />
        <ContentWrapper className="debit-card-election-content">
          <div className="content-section w-100">
            <div className="enroll-section">
              <FormRow className="mb-25">
                <FormColumn>
                  <h2 className="title-header">
                    {noMatchingOffer ? 'Never Miss a Payment' : 'Save Money with AutoPay'}
                  </h2>
                  <p className="autopay-subtitle">
                    {noMatchingOffer
                      ? 'Turn on autopay and let your payments run on autopilot.'
                      : !isAutoPayOffer
                        ? 'Wait! I changed my mind and want to SAVE MONEY with the NO INTEREST PROMOTION by enrolling in AutoPay! (Your loan terms will be updated automatically with the best available terms if you sign up for AutoPay)'
                        : 'Choose your preferred payment method to enroll in AutoPay.'}
                  </p>
                </FormColumn>
              </FormRow>
              <FormRow className="mb-1">
                <FormColumn>
                  {renderMethodOption(2)}
                </FormColumn>
                <FormColumn>
                  {renderMethodOption(1)}
                </FormColumn>
              </FormRow>
              {!isAutoPayOffer && (
                <FormRow>
                  <FormColumn>
                    <button
                      type="button"
                      className={`noAutoPayBtn ${values.isSelfSubmitted === 3 ? 'selected' : ''}`}
                      onClick={handleCheckboxChange.bind(null, 'isSelfSubmitted', 3)}
                    >
                      <span>×</span>
                      Continue without AutoPay promotion
                    </button>
                  </FormColumn>
                </FormRow>
              )}
              {errors.isSelfSubmitted ? (
                <FormRow>
                  <FormColumn>
                    <span className="error">{errors.isSelfSubmitted}</span>
                  </FormColumn>
                </FormRow>
              ) : null}
            </div>
            <div className="form-box">
              {
                values.isSelfSubmitted === 2 && (
                  <>
                    {isBankAccountExisting && (
                      <div className="edit-container" onClick={toggleBankInfoEdit}>
                        <img src={values.isEditingBankInfo ? cancelEditIcon : editIcon} alt={values.isEditingDebitCard ? 'edit icon' : 'cancel edit icon'} />
                      </div>
                    )}
                    {values.accounts && values.accounts.length > 1 && !values.isEditingBankInfo ? (
                      <FormRow>
                        <FormColumn>
                          <Select
                            data={values.accounts.map((acc) => ({
                              value: acc.accountId, title: `${acc.accountType} #${acc.accountNumber.slice(-4)}`,
                            }))}
                            label="Pay from Account"
                            placeholder="Select..."
                            name="accountId"
                            onChange={handleInputChange}
                            isRequired
                            isErrorVisible={false}
                            value={get(values, 'accountId', '')}
                          />
                        </FormColumn>
                      </FormRow>
                    ) : null}
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            label="Name on Account"
                            name="autoBankAccount.accountHolderName"
                            value={get(values, 'autoBankAccount.accountHolderName', '') || ''}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'autoBankAccount.accountHolderName')}
                            errorMessage={get(errors, 'autoBankAccount.accountHolderName')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Select
                            label="Account Type"
                            name="autoBankAccount.accountType"
                            data={[
                              { value: 'Checking', title: 'Checking' },
                              { value: 'Savings', title: 'Savings' },
                            ]}
                            value={get(values, 'autoBankAccount.accountType', '')}
                            onChange={handleInputChange}
                            hasError={!!get(errors, 'autoBankAccount.accountType')}
                            errorMessage={get(errors, 'autoBankAccount.accountType')}
                            isDisabled={!values.isEditingBankInfo && isBankAccountExisting}
                            className="readOnly"
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <div className="position-relative">
                            <Input
                              type="tel"
                              label="Routing Number"
                              name="autoBankAccount.routingNumber"
                              value={get(values, 'autoBankAccount.routingNumber', '') || ''}
                              onChange={handleInputChange}
                              isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? routingNumberMask : undefined}
                              hasError={!!get(errors, 'autoBankAccount.routingNumber')}
                              errorMessage={get(errors, 'autoBankAccount.routingNumber')}
                              readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                              isRequired
                            />
                            {get(values, 'autoBankAccount.bankName') && routingValidateStatus.verified ? (
                              <div className="bankName">
                                <span>{get(values, 'autoBankAccount.bankName')}</span>
                              </div>
                            ) : null}
                            {routingValidateStatus.loading ? <span className="input-loader" /> : null}
                            {!routingValidateStatus.loading && routingValidateStatus.checkedFirstTime ? (
                              <span className="input-status">
                                <img
                                  src={`${routingValidateStatus.verified ? VerifiedIcon : WarningIcon}`}
                                  alt="input-status"
                                />
                              </span>
                            ) : null}
                          </div>
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                    <FormRow>
                      <FormColumn>
                        <FormGroup>
                          <Input
                            type="tel"
                            label="Account Number"
                            name="autoBankAccount.accountNumber"
                            value={get(values, 'autoBankAccount.accountNumber', '') || ''}
                            onChange={handleInputChange}
                            isMasked={(values.isEditingBankInfo || !isBankAccountExisting) ? accountMask : undefined}
                            hasError={!!get(errors, 'autoBankAccount.accountNumber')}
                            errorMessage={get(errors, 'autoBankAccount.accountNumber')}
                            readOnly={!values.isEditingBankInfo && isBankAccountExisting}
                            isRequired
                          />
                        </FormGroup>
                      </FormColumn>
                    </FormRow>
                  </>
                )
              }
              {hasError && (values.isSelfSubmitted === 1 || values.isSelfSubmitted === 2) && (
                <FormRow>
                  <FormColumn>
                    <div className="alert-container">
                      {errorMessage || 'The payment format used did not go through. Please try again or use another method.'}
                    </div>
                  </FormColumn>
                </FormRow>
              )}
            </div>
          </div>
          <div className="top-section">
            <div>
              <h2 className="title-header mb-12">Down Payment Information</h2>
              <p className="title-description">
                Your debit card is needed to collect your down payment at checkout (note: pre-paid debit cards or credit cards are not accepted). By clicking ‘Save &amp; Continue’ you authorize
                &nbsp;
                { merchantName }
                &nbsp;
                (“Merchant”) through its agents to process your down payment under these
                &nbsp;
                <span onClick={togglePopup} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#3989E3', fontWeight: 'bold' }}>
                  terms
                </span>
                .
              </p>
            </div>
            {isDebitCardExisting && (
              <div className="edit-container" onClick={toggleDebitCardEdit}>
                <img src={values.isEditingDebitCard ? cancelEditIcon : editIcon} alt={values.isEditingDebitCard ? 'edit icon' : 'cancel edit icon'} />
              </div>
            )}
          </div>
          <div className="content-section">
            <FormRow>
              <FormColumn offset={7}>
                <FormGroup>
                  <Input
                    label="Down Payment Required"
                    name="downPayment"
                    value={`${formatCurrency(downPayment || 0, 2)}`}
                    isRequired
                    isMasked={currencyMask}
                    isBadgeVisible={false}
                    readOnly
                    style={{ color: '#3989E3', fontWeight: 'bold' }}
                  />
                </FormGroup>
              </FormColumn>
              <FormColumn>
                <FormGroup>
                  <Input
                    label="Consent Date"
                    name="consentDate"
                    value={values.consentDate}
                    isBadgeVisible={false}
                    placeHolder="MM/DD/YYYY"
                    readOnly
                    isDisabled
                  />
                </FormGroup>
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <FormGroup>
                  <Input
                    label="Name"
                    name="debitCard.accountHolderName"
                    value={get(values, 'debitCard.accountHolderName', '') || ''}
                    onChange={handleInputChange}
                    hasError={!!get(errors, 'debitCard.accountHolderName')}
                    errorMessage={get(errors, 'debitCard.accountHolderName')}
                    isRequired
                    readOnly
                  />
                </FormGroup>
              </FormColumn>
            </FormRow>
            <FormRow className="debit-card-container">
              <FormColumn offset={12} className="inputWrapper">
                <CardNumberInput
                  className="card-number-input"
                  value={(!isDebitCardExisting || values.isEditingDebitCard) ? (get(values, 'debitCard.cardNumber') || null) : null}
                  lastFour={isDebitCardExisting && !values.isEditingDebitCard ? (get(values, 'debitCard.cardNumber') || null) : null}
                  allowedBrands={['visa', 'mastercard', 'discover']}
                  onChange={handleCardChange}
                  onComplete={handleCardComplete}
                  onBlur={handleCardBlur}
                  onBrandChange={handleCardBrandChange}
                  onCancel={isDebitCardExisting ? handleCardCancel : undefined}
                  onRevert={isDebitCardExisting ? handleCardRevert : undefined}
                  disabled={!values.isEditingDebitCard && isDebitCardExisting}
                  error={!!(get(errors, 'debitCard.cardNumber') || cardError)}
                  label="Debit Card Number"
                  placeholder=""
                  isRequired
                />
                {(get(errors, 'debitCard.cardNumber') || cardError) && (
                  <span className="error">{get(errors, 'debitCard.cardNumber') || cardError}</span>
                )}
                {!cardError && !get(errors, 'debitCard.cardNumber') && <small>Discover, Visa or Mastercard only</small>}
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  name="debitCard.expirationDate"
                  label="Expiration Date"
                  onChange={handleInputChange}
                  value={get(values, 'debitCard.expirationDate') || ''}
                  isMasked={[/\d/, /\d/, '/', /\d/, /\d/]}
                  isRequired
                  hasError={!!get(errors, 'debitCard.expirationDate')}
                  errorMessage={get(errors, 'debitCard.expirationDate')}
                  placeHolder="MM/YY"
                  readOnly={!values.isEditingDebitCard && isDebitCardExisting}
                  inputmode="numeric"
                />
              </FormColumn>
            </FormRow>
            {hasError && (
              <FormRow>
                <FormColumn>
                  <div className="alert-container">
                    {errorMessage || 'The payment format used did not go through. Please try again or use another method.'}
                  </div>
                </FormColumn>
              </FormRow>
            )}
            <FormRow className="payroll-date">
              <FormColumn>
                <hr className="separator pt-20" />
                <div>
                  <h3 className="payment-date-subtitle mb-25">
                    Select a date which matches your payroll date
                  </h3>
                </div>
                <Input
                  label="First Payment Date"
                  name="firstPaymentDate"
                  value={values.firstPaymentDate ? `${values.firstPaymentDate.format('MM/DD/YYYY')}` : ''}
                  hasError={errors.firstPaymentDate}
                  onFocus={handleFocus}
                  isRequired
                  readOnly
                />
              </FormColumn>
            </FormRow>
            <FormRow className="save-and-continue">
              <FormColumn>
                <Button
                  onClick={(e) => handleSubmitForm(false, e)}
                  className="btn btn-primary save-button"
                  title="Save And Continue"
                  isDisabled={isProgressVisible}
                />
              </FormColumn>
            </FormRow>
            <FormRow className="pb-25">
              <FormColumn>
                <p style={{ color: '#797a93', fontSize: 12 }}>
                  <em style={{ color: '#002147', fontStyle: 'normal' }}>
                    <b style={{ color: '#3989E3' }}>Please Note:</b>
                  </em>
                  &nbsp;
                  Enrolling in Auto-Pay is optional. The Bank&apos;s credit decision is not dependent on whether you enroll in Auto-Pay. You can change your method of payment at any time in the future.
                </p>
              </FormColumn>
            </FormRow>
          </div>
        </ContentWrapper>
      </Transition>
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
      {/* <!-- No Popup Modal --> */}
      {isNoAutopayPopupVisible && (
        <Modal toggle={toggleNoAutopayPopup}>
          <div className="ach-modal-content">
            <div className="d-flex justify-content-end align-items-center close-link-container" onClick={toggleNoAutopayPopup}>
              <span className="close-link">Keep my current selection</span>
              <img src={XIcon} alt="x icon" width="10px" />
            </div>
            <h2 className="noautopay-modal-title">
              {noMatchingOffer
                ? 'Never Miss a Payment'
                : 'Don\'t Lose Your No Interest Promotion!*'}
            </h2>
            <h2 className="noautopay-modal-subtitle">
              {noMatchingOffer
                ? 'Turn on autopay and let your payments run on autopilot!'
                : 'Sign up for optional AutoPay today!'}
            </h2>
            <div className="d-flex justify-content-center icon-container">
              <img src={NoAchIcon} alt="no ach icon" />
            </div>
            <p className="text-center banner-description">
              We
              &nbsp;
              <b>recommend</b>
              &nbsp;
              using a debit card to automatically
              <br />
              make your monthly payments. it&apos;s free and easy to use!
            </p>

            <div className="d-flex justify-content-center mt-4 noautopay-btn-container">
              <Button
                onClick={handleUseDebitCard}
                className="btn btn-primary save-button"
                title="Yes - Use Debit Card"
              />
            </div>
            <div className="snack-warning">
              Note:
              &nbsp;
              <i>Using AutoPay saves you the hassle of calling in, or mailing a check to make a payment.</i>
            </div>
            {!noMatchingOffer && (
              <p className="noautopay-footer-disclosure">
                * Your loan may have a No interest on Principal Option Promotion included. This promotion can save you money if you pay off the principal amount of the loan in full within the Promotional Period (&quot;Promotional Period&quot;). During the Promotional Period you will be responsible for making all of your monthly payments and your loan will accrue interest on monthly basis. If you pay off your loan within the Promotional Period, the monthly payments that you have made during this period, which includes accrued interest, will be deducted from the principal amount of the loan. Length of Promotional Periods vary, please review your loan agreement for full details.
              </p>
            )}
          </div>
        </Modal>
      )}
      {/* <!-- End Disclaimer Modal --> */}
      {
        isGiactPopupVisible && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>{giactMessage}</p>
              {giactPopupAttrs && giactPopupAttrs.allowRetry && (
                <div onClick={toggleGiactPopup} className="btn btn-red bottomMargin">TRY AGAIN</div>
              )}
              {giactPopupAttrs && giactPopupAttrs.allowToProceedWithoutAutopay && (
                <div onClick={(e) => handleSubmitForm(true, e)} className={!giactPopupAttrs.allowRetry ? 'btn btn-red' : 'btn btn-blue'}>Proceed without signing up with autopay</div>
              )}
            </div>
          </Modal>
        )
      }
      {
        isDatePickVisible && (
          <Modal>
            <Datetime
              input={false}
              className="dateWrapper"
              initialValue={values.firstPaymentDate}
              value={firstPaymentDate}
              isValidDate={(current) => (
                current.isAfter(Datetime.moment().add(36, 'd'))
                && current.isBefore(Datetime.moment().add(47, 'd'))
              )}
              onChange={handleDateChange}
              dateFormat="MM/DD/YYYY"
              displayTimeZone="America/Los_Angeles"
              timeFormat={false}
            />
            <div style={{ padding: 10, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={handleCloseModal}
                className="btn btn-md btn-blue-outline"
                title="Close"
              />
              <Button
                onClick={handleConfirmDate}
                className="btn btn-md btn-blue"
                title="Confirm"
              />
            </div>
          </Modal>
        )
      }
      {
          isDisclosureVisible && (
            <Modal>
              <div className="card-content-definition">
                <div className="card-header">
                  <h5>View Details</h5>
                </div>
              </div>
              <div className="close" onClick={handleClosePopup} />
              <div className="card-content diclosure-card">
                You represent and warrant that you are authorized to use the debit card you have provided for the down payment on your purchase from Merchant (“Debit Card”). You are authorizing Cross River Bank, as agent of Merchant, to charge your Debit Card in the amount of the down payment.
                <br />
                You understand that your Debit Card may be charged prior to you signing your loan agreement. If you do not sign your loan agreement within thirty (30) days of the date that you applied for this loan, your loan offer will expire and the lender will have no obligation to provide you the loan. In that event, Merchant will refund the down payment to you by electronic funds transfer to your bank. If you cancel your purchase with Merchant prior to signing your loan agreement, you can request a refund of the down payment made to Merchant by calling (800) 994-6177 ext. 0.
              </div>
            </Modal>
          )
        }
    </section>
  );
};

DebitCardElection.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default Validator(schema)(connect((state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
}))(DebitCardElection));
